import { APP_INITIALIZER, ApplicationConfig, inject, isDevMode } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withEnabledBlockingInitialNavigation } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { routes } from './app.routes';
import { authReducer } from './core/store/auth/auth.reducer';
import { AuthEffects } from './core/store/auth/auth.effects';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { gameSessionReducer } from './core/store/game-session/game-session.reducer';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { GoogleLoginProvider, SocialAuthServiceConfig } from '@abacritt/angularx-social-login';
import { environment } from '../environments/environment';
import { TranslationService, isSupportedLanguage } from './core/services/translation.service';
import { StorageService } from './core/services/storage.service';
import { Location } from '@angular/common';
import { errorNormalizerInterceptor } from './core/interceptors/error-normalizer.interceptor';

const scrollConfig = {
  scrollPositionRestoration: 'top' as const,
};

const windowAvailable = typeof window !== 'undefined';

function preloadTranslationsFactory(translationService: TranslationService, storageService: StorageService, location: Location) {
  return () => {
    const path = windowAvailable ? window.location.pathname : location.path();
    // console.log('🌐 Raw location.pathname:', path);

    const pathParts = path.split('/').filter(Boolean);
    // console.log('📦 Path parts:', pathParts);

    const urlLang = pathParts[0] && pathParts[0].length === 2 ? pathParts[0] : '';
    const savedLang = storageService.getLanguage();
    const browserLang = navigator.language.split('-')[0];

    // console.log('💾 Saved lang:', savedLang);
    // console.log('🌍 Browser lang:', browserLang);
    // console.log('🌐 URL lang:', urlLang);

    // ✅ Case 1: URL already contains supported lang (http://localhost:4200/uk/profile/my-profile)
    if (isSupportedLanguage(urlLang)) {
      // console.log('✅ Using language from URL:', urlLang);
      // en in URL replace without en part
      if (urlLang === 'en') {
        // console.log('➡️ Redirecting en to no en');
        storageService.setLanguage('en');
        if (windowAvailable) {
          window.location.replace(`/${pathParts.slice(1).join('/')}`);
        }
        return translationService.loadTranslations('en');
      }
      return translationService.loadTranslations(urlLang);
    }

    // ✅ Case 2: URL contain unsupported lang (http://localhost:4200/cz/profile/my-profile)
    if (urlLang && !isSupportedLanguage(urlLang)) {
      // console.log('➡️ Redirecting unsupported lang to en');
      storageService.setLanguage('en');
      if (windowAvailable) {
        window.location.replace(`/${pathParts.slice(1).join('/')}`);
      }
      return translationService.loadTranslations('en');
    }

    // 🛠️ Build redirect path
    const restOfPath = pathParts.join('/');
    const redirectTo = (lang: string) => `/${lang}${restOfPath ? '/' + restOfPath : ''}`;

    // ✅ Case 3: SavedLang exists (even 'en') → use it, don't check browserLang http://localhost:4200/profile/my-profile
    // if (savedLang && isSupportedLanguage(savedLang)) {
    //   if (savedLang !== 'en') {
    //     console.log('➡️ Redirecting using savedLang:', savedLang);
    //     window.location.replace(redirectTo(savedLang));
    //     return Promise.resolve();
    //   }
    //   // If savedLang is 'en' — don't redirect, just load
    //   console.log('SavedLang en. loadTranslations en');
    //   return translationService.loadTranslations('en');
    // }

    // // ✅ Case 4: Use browserLang if supported and not 'en' http://localhost:4200/profile/my-profile
    // if (isSupportedLanguage(browserLang) && browserLang !== 'en') {
    //   console.log('➡️ Redirecting using browserLang:', browserLang);
    //   window.location.replace(redirectTo(browserLang));
    //   return Promise.resolve();
    // }

    // ✅ Case 5: Use browserLang if supported, no browserLang and not 'en' http://localhost:4200/profile/my-profile
    if (windowAvailable && !savedLang && isSupportedLanguage(browserLang) && browserLang !== 'en') {
      // console.log('➡️ Redirecting using browserLang:', browserLang);
      storageService.setLanguage(browserLang);
      window.location.replace(redirectTo(browserLang));

      return translationService.loadTranslations(browserLang); // even we reload page, we need it to see correct view during reloading
    }

    // console.log('🛑 Fallback to EN, no redirect');
    return translationService.loadTranslations('en');
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withInMemoryScrolling(scrollConfig), withEnabledBlockingInitialNavigation()),
    provideAnimations(),
    provideHttpClient(
      withInterceptors([
        AuthInterceptor,
        errorNormalizerInterceptor
      ])
    ),
    provideStore({
      auth: authReducer,
      gameSession: gameSessionReducer
    }),
    provideEffects([AuthEffects]),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: false,
      traceLimit: 75
    }),
    {
      provide: 'SocialAuthServiceConfig',
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(
              environment.googleClientId,
              {
                oneTapEnabled: false   // ← turn off the One-Tap/popup
              }
            )
          }
        ],
        onError: (err) => {
          console.error(err)
        },
      } as SocialAuthServiceConfig
    },
    {
      provide: APP_INITIALIZER,
      useFactory: preloadTranslationsFactory,
      deps: [TranslationService, StorageService, Location],
      multi: true
    },
    provideClientHydration(withEventReplay())
  ]
};
